import api from './api';
import { hashSiret } from '../utils/hash';

/**
 * Service API pour la gestion des organisations
 * @module services/organizations
 */

/**
 * Rechercher une organisation par SIRET ou nom
 * @param {Object} params - Paramètres de recherche
 * @param {string} [params.siret] - Numéro SIRET (14 chiffres)
 * @param {string} [params.name] - Nom de l'organisation
 * @returns {Promise<Object>} Organisation trouvée ou suggestions
 */
export async function searchOrganization({ siret, name }) {
  try {
    const response = await api.post('/organizations/search', {
      siret,
      name
    });

    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Erreur recherche organisation:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Erreur lors de la recherche',
      details: error.response?.data
    };
  }
}

/**
 * Créer une nouvelle organisation
 * @param {Object} data - Données de l'organisation
 * @param {string} data.siret - Numéro SIRET (14 chiffres)
 * @param {string} data.name - Nom de l'organisation
 * @param {string} [data.address] - Adresse
 * @param {string} [data.city] - Ville
 * @param {string} [data.postalCode] - Code postal
 * @param {string} [data.country] - Pays (défaut: 'France')
 * @param {string} [data.website] - Site web
 * @param {string} [data.phone] - Téléphone
 * @param {Object} [data.metadata] - Métadonnées additionnelles
 * @returns {Promise<Object>} Organisation créée
 */
export async function createOrganization(data) {
  try {
    const response = await api.post('/organizations/create', {
      siret: data.siret,
      name: data.name,
      address: data.address,
      city: data.city,
      postalCode: data.postalCode,
      country: data.country || 'France',
      website: data.website,
      phone: data.phone,
      metadata: data.metadata || {}
    });

    return {
      success: true,
      data: response.data.organization
    };
  } catch (error) {
    console.error('Erreur création organisation:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Erreur lors de la création',
      details: error.response?.data
    };
  }
}

/**
 * Récupérer les détails d'une organisation par ID
 * @param {string} organizationId - ID UUID de l'organisation
 * @returns {Promise<Object>} Détails de l'organisation
 */
export async function getOrganization(organizationId) {
  try {
    const response = await api.get(`/organizations/${organizationId}`);

    return {
      success: true,
      data: response.data.organization
    };
  } catch (error) {
    console.error('Erreur récupération organisation:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Organisation introuvable',
      details: error.response?.data
    };
  }
}

/**
 * Lister les organisations (avec pagination)
 * @param {Object} params - Paramètres de pagination et filtres
 * @param {number} [params.page=1] - Numéro de page
 * @param {number} [params.limit=20] - Nombre d'éléments par page
 * @param {string} [params.size] - Filtrer par taille ('small', 'medium', 'large')
 * @param {boolean} [params.isVerified] - Filtrer par statut de vérification
 * @returns {Promise<Object>} Liste des organisations avec pagination
 */
export async function listOrganizations({ page = 1, limit = 20, size, isVerified } = {}) {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    });

    if (size) params.append('size', size);
    if (typeof isVerified === 'boolean') params.append('isVerified', isVerified.toString());

    const response = await api.get(`/organizations?${params.toString()}`);

    return {
      success: true,
      data: response.data.organizations,
      pagination: {
        page: response.data.page,
        limit: response.data.limit,
        total: response.data.total,
        totalPages: response.data.totalPages
      }
    };
  } catch (error) {
    console.error('Erreur liste organisations:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Erreur lors de la récupération',
      details: error.response?.data
    };
  }
}

/**
 * Vérifier si un SIRET est valide (14 chiffres)
 * @param {string} siret - Numéro SIRET à valider
 * @returns {boolean} True si valide
 */
export function isValidSiret(siret) {
  if (!siret) return false;
  const cleaned = siret.replace(/\s/g, '');
  return /^\d{14}$/.test(cleaned);
}

/**
 * Formater un SIRET pour l'affichage (XXX XXX XXX XXXXX)
 * @param {string} siret - Numéro SIRET
 * @returns {string} SIRET formaté
 */
export function formatSiret(siret) {
  if (!siret) return '';
  const cleaned = siret.replace(/\s/g, '');
  if (cleaned.length !== 14) return siret;
  return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6, 9)} ${cleaned.slice(9)}`;
}

/**
 * Obtenir le hash anonymisé d'un SIRET (pour analytics)
 * @param {string} siret - Numéro SIRET
 * @returns {string|null} Hash SHA256 du SIRET
 */
export function getAnonymizedSiret(siret) {
  if (!isValidSiret(siret)) return null;
  return hashSiret(siret);
}

export default {
  searchOrganization,
  createOrganization,
  getOrganization,
  listOrganizations,
  isValidSiret,
  formatSiret,
  getAnonymizedSiret
};
